import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, Loading, ErrorState, SectionCard, fmtINR, fmtDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PERIODS = [["today", "Today"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["all", "All time"], ["custom", "Custom dates"]];
const TOTALS = [["taxable_value", "Taxable value"], ["cgst", "CGST"], ["sgst", "SGST"], ["product_gst_total", "Product GST"], ["fee_gst_total", "Fee GST"], ["bill_total", "Bill total"]];

function buildQuery(f) {
  const q = new URLSearchParams({ period: f.period, level: f.level, page: f.page, limit: 50 });
  if (f.period === "custom") { if (f.from) q.set("from", f.from); if (f.to) q.set("to", f.to); }
  return q.toString();
}

function Filters({ f, set }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <Select value={f.period} onValueChange={(v) => set({ ...f, period: v, page: 1 })}>
        <SelectTrigger className="w-40 h-9" data-testid="gst-period"><SelectValue /></SelectTrigger>
        <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      {f.period === "custom" && <>
        <Input type="date" className="w-40 h-9" value={f.from} onChange={(e) => set({ ...f, from: e.target.value, page: 1 })} data-testid="gst-from" />
        <Input type="date" className="w-40 h-9" value={f.to} onChange={(e) => set({ ...f, to: e.target.value, page: 1 })} data-testid="gst-to" />
      </>}
      <Select value={f.level} onValueChange={(v) => set({ ...f, level: v, page: 1 })}>
        <SelectTrigger className="w-36 h-9" data-testid="gst-level"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="bill">Bill-wise</SelectItem><SelectItem value="item">Item-wise</SelectItem></SelectContent>
      </Select>
    </div>
  );
}

function Rows({ level, rows }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground" data-testid="gst-empty">No bills in this period.</p>;
  return (
    <div className="overflow-x-auto"><table className="w-full min-w-[820px] text-sm" data-testid="gst-table">
      <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-2">Bill</th><th>Date</th><th>Shop</th>{level === "item" && <th>Item</th>}<th>Taxable</th><th>CGST</th><th>SGST</th><th>{level === "item" ? "GST" : "Bill total"}</th></tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={`${r.order_number}-${i}`} className="border-t">
          <td className="py-1.5 font-mono text-xs">{r.order_number}</td><td>{fmtDate(r.bill_date)}</td><td>{r.shop_name}</td>
          {level === "item" && <td>{r.product}{r.gst_rate != null ? ` · ${r.gst_rate}%` : ""}</td>}
          <td className="tabular-nums">{fmtINR(r.taxable_value)}</td><td className="tabular-nums">{fmtINR(r.cgst)}</td><td className="tabular-nums">{fmtINR(r.sgst)}</td>
          <td className="tabular-nums">{fmtINR(level === "item" ? r.gst_total : r.bill_total)}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

export function GstReport() {
  const [f, setF] = useState({ period: "30d", level: "bill", from: "", to: "", page: 1 });
  const qs = buildQuery(f);
  const ready = f.period !== "custom" || (f.from && f.to);
  const { data, loading, error, refetch } = useFetch(ready ? `/admin/gst/report?${qs}` : null, [qs, ready]);
  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/gst/report?${qs}&format=csv`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `gst-${f.level}-${f.period}.csv`; a.click();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <SectionCard className="p-5" data-testid="gst-report">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-semibold font-outfit">GST report</h3>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportCsv} disabled={!ready} data-testid="gst-export-csv"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      </div>
      <Filters f={f} set={setF} />
      {!ready ? <p className="text-sm text-muted-foreground">Pick both dates.</p> : loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : (<>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">{TOTALS.map(([k, l]) => (
          <div key={k} className="rounded-md border p-2" data-testid={`gst-total-${k}`}><div className="text-xs text-muted-foreground">{l}</div><div className="font-medium tabular-nums">{fmtINR(data.totals?.[k] || 0)}</div></div>))}</div>
        <p className="text-xs text-muted-foreground mb-2" data-testid="gst-bill-count">{data.bill_count} bills</p>
        <Rows level={f.level} rows={data.rows || []} />
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="gst-prev">Previous</Button>
          <Button size="sm" variant="outline" disabled={(data.rows || []).length < 50} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="gst-next">Next</Button>
        </div>
        {(data.notes || []).map((n) => <p key={n} className="text-xs text-muted-foreground mt-2">{n}</p>)}
      </>)}
    </SectionCard>
  );
}
